/**
 * Vehicle-finance repayment (flat monthly rupee-per-₹100 interest).
 *
 *   Balance (principal)  B = Total amount − Out payment
 *   Interest / month     = B × (Rate ÷ 100)
 *   Total interest       = B × (Rate ÷ 100) × Months
 *   Monthly EMI          = (B + Total interest) ÷ Months
 *
 * Rate is rupees per ₹100 principal per month (e.g. ₹1 on ₹1,00,000 → ₹1,000/month).
 */
import { parseAmountInput } from './formatInr';

export interface FinanceCalcInput {
  totalAmount?: string | number;
  amount?: string | number;
  outPayment?: string | number;
  interestPercentage?: string | number;
  emiPeriod?: string | number;
}

export interface FinanceCalcResult {
  principal: number;
  outPayment: number;
  balance: number;
  months: number;
  /** Rupees per ₹100 principal per month. */
  rupeesPerHundred: number;
  interestPerMonth: number;
  totalInterest: number;
  totalPayable: number;
  emi: number;
  valid: boolean;
}

function num(v: string | number | undefined | null): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  return parseAmountInput(String(v ?? ''));
}

export function computeFinance(input: FinanceCalcInput): FinanceCalcResult {
  const principal = num(input.totalAmount) || num(input.amount);
  const outPayment = num(input.outPayment);
  const balance = Math.max(0, principal - outPayment);
  const months = Math.max(0, Math.round(num(input.emiPeriod)));
  const rate = num(input.interestPercentage);

  const interestPerMonth = balance > 0 && rate > 0 ? Math.round(balance * (rate / 100)) : 0;
  const totalInterest = months > 0 && interestPerMonth > 0 ? Math.round(interestPerMonth * months) : 0;
  const totalPayable = balance + totalInterest;
  const emi = months > 0 ? Math.round(totalPayable / months) : 0;

  return {
    principal,
    outPayment,
    balance,
    months,
    rupeesPerHundred: rate,
    interestPerMonth,
    totalInterest,
    totalPayable,
    emi,
    valid: balance > 0 && months > 0 && rate > 0,
  };
}

export const inr = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

function ymdToMs(ymd: string): number {
  if (!ymd) return NaN;
  const trimmed = ymd.trim();
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? `${trimmed}T12:00:00` : trimmed;
  return new Date(iso).getTime();
}

/** Calendar days between two dates (inclusive of partial days, same basis as overdue). */
export function daysBetweenYmd(fromYmd: string, toYmd: string): number {
  const from = ymdToMs(fromYmd);
  const to = ymdToMs(toYmd);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
  return Math.max(0, Math.ceil((to - from) / 86400000));
}

export type OutPaymentDue = {
  principal: number;
  interest: number;
  total: number;
  days: number;
  monthlyInterest: number;
};

/**
 * Out-payment settlement: principal + interest accrued from agreement date.
 * Rate is rupees per ₹100 out-payment principal per month (same as OP interest on form).
 */
export function computeOutPaymentDue(input: {
  outPayment: number;
  opRate: number;
  startDate: string;
  asOfDate: string;
}): OutPaymentDue {
  const principal = Math.max(0, Math.round(num(input.outPayment)));
  const rate = Math.max(0, num(input.opRate));
  const startYmd = String(input.startDate || '').slice(0, 10);
  const asOfYmd = String(input.asOfDate || '').slice(0, 10);
  const days = daysBetweenYmd(startYmd, asOfYmd);
  const monthlyInterest = principal > 0 && rate > 0 ? Math.round(principal * (rate / 100)) : 0;
  const interest = Math.round(monthlyInterest * (days / 30) * 100) / 100;
  return {
    principal,
    interest,
    total: principal + interest,
    days,
    monthlyInterest,
  };
}

/** True when ledger shows out-payment principal fully collected. */
export function isOutPaymentCollected(
  recordId: string,
  payments: { recordId: string; entryType?: string; principalPortion?: number; amount?: number }[],
  outPaymentPrincipal: number
): boolean {
  if (outPaymentPrincipal <= 0) return true;
  const collected = payments
    .filter((p) => p.recordId === recordId && p.entryType === 'out_payment')
    .reduce((sum, p) => sum + Math.max(0, Number(p.principalPortion) || Number(p.amount) || 0), 0);
  return collected >= outPaymentPrincipal - 0.5;
}
